/**
 * FIVE ROOMS: a second front page, built as a walk through five places.
 *
 * Scroll down and you travel sideways through a village at dusk, a garden at
 * noon, a room indoors, the sea floor and orbit. Arrive in one and the things
 * in it get built where they stand: the sentence types out above the spot and
 * the sprite lands a part at a time. Click anything and you get the sentence it
 * came from, its parts in draw order, and its colour table.
 *
 * The rooms are drawn by the same renderer as the sprites, from specs in
 * scenes.ts, at the size the screen needs. One scale enlarges the room and
 * everything standing in it, so positions are given in the room's own pixel
 * grid: a mug on a table is a mug whose baseline is the table's height, and it
 * lands on the table at every screen size.
 *
 * The page scrolls normally and the world is moved sideways by however far down
 * you are. Taking over the wheel would break the keyboard, the scrollbar and
 * the phone.
 */

import { kitFrom } from "../src/kit.js";
import { bitmapOf, renderFrames, type RenderedFrame } from "../src/render.js";
import { fitScale } from "../src/raster.js";
import type { SpriteSpec } from "../src/spec.js";

import { EXAMPLES, GALLERY } from "./bundled.js";
import { SCENES, TILE, roomSize, shadowSpec, type Scene } from "./scenes.js";
import { PROPS } from "./props.js";
import { css } from "./colour.js";
import { blit } from "./paint.js";
import { fillPacks } from "./packs.js";

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T | null;


interface Thing {
	spec: SpriteSpec;
	said: string;
	mine?: boolean;
}

// ---- the rooms ---------------------------------------------------------------------

interface Placed {
	/** A gallery or bundled example name. */
	name: string;
	/**
	 * Where along the floor, 0 to 1, snapped to the nearest tile.
	 *
	 * A fraction rather than a tile number because how many tiles fit depends on
	 * the window, and a room laid out in absolute columns bunches everything at
	 * the left of a wide screen.
	 */
	x: number;
	/**
	 * Whole tiles left or right of that.
	 *
	 * Furniture is arranged in groups, not spread out evenly: a bed with its
	 * drawers beside it, a fire with a chair pulled up to it. A group is anchored
	 * by one fraction and its members sit at tile offsets from it, so they stay
	 * together at any width instead of drifting apart on a wide screen. Placing
	 * everything by its own fraction is what made the room look scattered.
	 */
	dx?: number;
	/**
	 * Which tile row, 0 at the back wall.
	 *
	 * This is the depth, and it does two things at once: it puts the thing
	 * further down the screen, and it puts it in front of everything in a row
	 * behind it. Overlap is what makes a floor look like a floor.
	 */
	row?: number;
	/** Stand it on top of another thing in this room, by name. */
	on?: string;
	/** Hang it on the back wall, this many rows above the floor line. */
	wall?: number;
	/**
	 * A thing that lies on the floor rather than standing on it: a rug, a ring of
	 * stones, a table seen from above.
	 *
	 * It gets no shadow, because nothing flat casts one, and it sits behind
	 * anything standing in the same row so a chair can be pushed under a table.
	 */
	flat?: boolean;
	/**
	 * Draw it at half the room's scale.
	 *
	 * For the things that belong on a surface rather than on the floor. A mug
	 * asked for at 16x16 came back 32x32, which at room scale is a mug the size
	 * of a chest of drawers. Halving the scale is exact while the room scale is
	 * even, and it beats redrawing until the model returns the size asked for.
	 */
	small?: boolean;
}

interface Room {
	id: string;
	/** Shown in the corner while you are in it. */
	name: string;
	/** The line that introduces the room. */
	line: string;
	holds: Placed[];
	/** A sprite stamped edge to edge across the floor. */
	floorTile?: string;
	/** A sprite stamped along the bottom of the back wall. */
	wallTile?: string;
	/**
	 * A sprite tiled across everything above the wall.
	 *
	 * The skies were hand-authored gradient bands in the scene spec, and looked
	 * it. This is the same argument as the floor: if the room is meant to prove
	 * the tool drew everything, the sky cannot be the one thing I drew.
	 */
	skyTile?: string;
	/**
	 * Sprites that cross the room on a loop: clouds, birds, fish, a distant ship.
	 *
	 * `of` is the set to draw from, cycled along the lane so the same cloud does
	 * not come round again and again. `every` is how many room pixels apart the
	 * copies are, `secs` how long one crossing takes. Two copies of the strip sit
	 * side by side and the pair is slid by exactly one strip width, so the loop
	 * has no seam.
	 */
	drifts?: { of: string[]; y: number; every: number; secs: number; back?: boolean; flip?: boolean }[];
	/**
	 * Whether to flood everything above the wall tile with the tile's own top
	 * colour.
	 *
	 * Right for a room, where the wall carries on up out of shot. Wrong outdoors,
	 * where the tile is a low boundary wall and what is above it is the sky.
	 */
	wallFill?: boolean;
	/**
	 * Repeat the wall tile all the way up instead of flooding a colour.
	 *
	 * For a wall with no top and bottom to it, like sheet metal. Flooding uses the
	 * tile's topmost pixel, which on the hull came out far paler than the tile
	 * reads, and left the upper wall looking like a different material. A tile
	 * with skirting along its bottom still has to be flooded, or you get a wall
	 * made of skirting boards.
	 */
	wallRepeat?: boolean;
	/**
	 * How many floating things this room's air has, and what they are called.
	 * Each room's CSS gives that class its own look and drift.
	 */
	air?: { kind: string; count: number };
	/** How many stars its sky has. Only the two dark rooms have any. */
	stars?: number;
}

/**
 * Five places, in the order you walk through them.
 *
 * Grouped so each room has a reason to look different from the last: dusk, then
 * daylight, then indoors, then underwater, then no gravity at all. Each sprite
 * is put where that thing would actually be, which is the point: a rug lies on
 * the floor, a mug stands on the table, a door is in the wall.
 */
const ROOMS: Room[] = [
	{
		id: "dusk",
		name: "The village, at dusk",
		line: "Every cobble, cart and lantern here began as a sentence. And that fire genuinely flickers, it is not a picture of one.",
		floorTile: "cobble_floor",
		wallTile: "yard_wall",
		skyTile: "sky_dusk",
		drifts: [
			{ of: ["cloud_a", "cloud_b", "cloud_d"], y: 0.62, every: 150, secs: 190 },
			{ of: ["cloud_c", "cloud_small", "cloud_c"], y: 0.84, every: 110, secs: 130, back: true },
		],
		// The wall here is a low boundary wall with a sky above it, so nothing is
		// flooded in over the top of it.
		wallFill: false,
		holds: [
			// Along the wall.
			{ name: "lantern_post", x: 0.067, row: 0 },
			{ name: "stone_well", x: 0.267, row: 0 },
			{ name: "hay_bale", x: 0.533, row: 0 },
			{ name: "brazier", x: 0.733, row: 0 },
			{ name: "market_stall", x: 0.933, row: 0 },

			// A step into the yard.
			{ name: "sack_pile", x: 0.133, row: 1 },
			{ name: "water_trough", x: 0.4, row: 1 },
			{ name: "camp_fire", x: 0.667, row: 1 },
			{ name: "crate_stack", x: 0.867, row: 1 },

			// Nearest the front, overlapping everything behind.
			{ name: "signpost", x: 0.2, row: 2 },
			{ name: "handcart", x: 0.333, row: 2 },
			{ name: "wooden_barrel", x: 0.467, row: 2 },
			{ name: "yard_bench", x: 0.8, row: 2 },
		],
		air: { kind: "ember", count: 16 },
		stars: 40,
	},
	{
		id: "garden",
		name: "The garden, at noon",
		line: "Different afternoon, same hand. Ask for a beehive after a chicken coop and it turns up in matching paint.",
		floorTile: "grass_floor",
		wallTile: "hedge_wall",
		skyTile: "sky_day",
		drifts: [
			{ of: ["cloud_a", "cloud_d", "cloud_b", "cloud_big"], y: 0.6, every: 140, secs: 170 },
			{ of: ["cloud_c", "cloud_small"], y: 0.82, every: 95, secs: 120 },
			{ of: ["bird_pair", "bird_one", "bird_one"], y: 0.7, every: 190, secs: 70, back: true, flip: true },
		],
		wallFill: false,
		holds: [
			// The growing end.
			{ name: "veg_bed", x: 0.1, row: 1 },
			{ name: "scarecrow", x: 0.1, dx: 1, row: 0 },
			{ name: "watering_can", x: 0.1, dx: 3, row: 2 },
			{ name: "compost_heap", x: 0.1, dx: -1, row: 0 },

			// The keeping end.
			{ name: "chicken coop", x: 0.46, row: 0 },
			{ name: "beehive", x: 0.46, dx: 2, row: 0 },
			{ name: "wheelbarrow", x: 0.46, dx: -2, row: 1 },
			{ name: "feed_sack", x: 0.46, dx: 3, row: 1 },

			// The sitting end.
			{ name: "apple_tree", x: 0.84, row: 0 },
			{ name: "garden_bench", x: 0.84, dx: -1, row: 1 },
			{ name: "plant", x: 0.84, dx: 2, row: 1 },
			{ name: "flower_bed", x: 0.84, dx: 1, row: 2 },
		],
		air: { kind: "leaf", count: 12 },
	},
	{
		id: "inside",
		name: "Inside, lamps lit",
		line: "Pick anything up and it comes apart in your hands. The fireplace is thirteen pieces and fifteen colours, and all of them do as they are told.",
		floorTile: "floor_boards",
		wallTile: "wall_panel",
		// Arranged in three groups with bare floor between them, the way a room is
		// actually furnished. Each group is anchored on one fraction and built out
		// from it in whole tiles.
		holds: [
			// The sleeping corner.
			// A tile forward of the wall, because a bed is deep: its front edge is
			// where it meets the floor, and the head reaches back to the wall. Placing
			// it on the wall line stood the whole thing up the wall instead.
			{ name: "cosy_bed", x: 0.08, row: 1 },
			{ name: "drawers", x: 0.08, dx: 2, row: 0 },
			{ name: "floor_lamp", x: 0.08, dx: -1, row: 1 },
			{ name: "small_crate", x: 0.08, dx: 3, row: 1 },

			// The hearth, with a chair pulled up to it and a rug in front.
			{ name: "fireplace", x: 0.45, row: 0 },
			{ name: "log_basket", x: 0.45, dx: 2, row: 0 },
			{ name: "wall_window", x: 0.45, dx: -2, wall: 50 },
			// On the far side of the hearth. The left corner belongs to the title.
			{ name: "framed_art", x: 0.45, dx: 4, wall: 34 },
			{ name: "round_patterned_rug", x: 0.45, row: 1, flat: true },
			{ name: "wooden_chair", x: 0.45, dx: -2, row: 1 },
			{ name: "potted_fern", x: 0.45, dx: 3, row: 1 },

			// The study end, and the way out.
			{ name: "bookshelf", x: 0.8, row: 0 },
			{ name: "tall_clock", x: 0.8, dx: 2, row: 0 },
			{ name: "wooden door", x: 0.8, dx: 4, wall: 0 },
			{ name: "dining_table", x: 0.8, dx: 1, row: 2 },
			// The whole reason positions are in the room's own grid.
			{ name: "tea_mug", x: 0.8, dx: 1, row: 2, on: "dining_table", small: true },
		],
		air: { kind: "dust", count: 20 },
	},
	{
		id: "water",
		name: "The sea floor",
		line: "The same chest at any size, in any colours you fancy. Silver treasure? Odd choice, but it is your shipwreck.",
		floorTile: "sand_floor",
		wallTile: "reef_wall",
		skyTile: "sky_sea",
		drifts: [
			// Drawn facing left, so the lane that travels right is mirrored.
			{ of: ["sea_fish", "sea_fish_c", "sea_fish_b"], y: 0.34, every: 130, secs: 95 },
			{ of: ["sea_fish_b", "sea_fish_c"], y: 0.68, every: 170, secs: 145, back: true, flip: true },
		],
		wallFill: false,
		holds: [
			// The wreck.
			{ name: "wreck_ribs", x: 0.12, row: 0 },
			{ name: "ships_anchor", x: 0.12, dx: 2, row: 1 },
			{ name: "sunken_barrel", x: 0.12, dx: -1, row: 2 },

			// The hoard.
			{ name: "treasure_chest_sunk", x: 0.48, row: 1 },
			{ name: "amphora", x: 0.48, dx: 2, row: 0 },
			{ name: "coral_fan", x: 0.48, dx: -2, row: 0 },
			{ name: "conch_shell", x: 0.48, dx: 1, row: 2 },

			// The garden of it.
			{ name: "kelp_stand", x: 0.84, row: 0 },
			{ name: "giant_clam", x: 0.84, dx: -1, row: 1 },
			{ name: "sea_urchin", x: 0.84, dx: 2, row: 2 },
		],
		air: { kind: "bubble", count: 22 },
	},
	{
		id: "orbit",
		name: "Orbit",
		line: "All cut to tile size and packed ready to leave. Take the lot home, drop it in your game, nobody need ever know.",
		floorTile: "deck_floor",
		wallTile: "hull_wall",
		wallRepeat: true,
		holds: [
			// The bridge.
			{ name: "control_console", x: 0.12, row: 0 },
			{ name: "pilot_seat", x: 0.12, dx: 1, row: 1 },
			{ name: "floor_hatch", x: 0.12, dx: 3, row: 2, flat: true },

			// Engineering.
			{ name: "power_cell", x: 0.46, row: 0 },
			{ name: "supply_locker", x: 0.46, dx: 2, row: 0 },
			{ name: "coolant_pipes", x: 0.46, dx: -2, row: 0 },
			{ name: "tool_crate", x: 0.46, dx: 1, row: 1 },

			// The growing bay, because a ship has to eat.
			{ name: "hydro_tray", x: 0.8, row: 1 },
			{ name: "service_droid", x: 0.8, dx: 2, row: 1 },
			{ name: "airlock_door", x: 0.8, dx: 1, wall: 0 },
			{ name: "ship_window", x: 0.4, wall: 18 },
		],
		air: { kind: "spark", count: 26 },
	},
];

/**
 * Sentences for the bundled examples.
 *
 * The gallery carries the sentence that actually produced each of its sprites.
 * The bundled examples predate that and have none, so these are written down
 * here rather than invented at render time, and they are what those sprites
 * would have been asked for.
 */
const SAID: Record<string, string> = {
	plant: "a potted plant with broad leaves",
	"chicken coop": "a wooden chicken coop with a lamp",
	mug: "a chipped ceramic mug",
	battery: "a heavy industrial battery cell",
	missile: "a small guided missile on a stand",
	"wooden door": "a heavy wooden door with iron bands",
};

/** A spec by name: the room props first, then the gallery, then the examples. */
function findSpec(name: string): Thing | null {
	const prop = PROPS.find((p) => p.spec.name === name);
	if (prop) return { spec: prop.spec, said: prop.want };
	const fromGallery = GALLERY.find((g) => g.spec.name === name);
	if (fromGallery) return { spec: fromGallery.spec, said: fromGallery.want };
	const example = EXAMPLES[name];
	if (example) return { spec: example, said: SAID[name] ?? name.replace(/_/g, " ") };
	return null;
}

// ---- picking something up ------------------------------------------------------------

const fit = (spec: SpriteSpec, room: number, cap = 12) => fitScale(spec.size[0], spec.size[1], room, room, cap);

/**
 * What a thing in the scene is made of.
 *
 * A dialog rather than a permanent side panel, so the scene stays the page and
 * the detail only appears when you ask for it.
 */
function open(thing: Thing) {
	const dialog = document.createElement("dialog");
	dialog.className = "sheetbox lookbox";

	const shut = () => {
		dialog.close();
		dialog.remove();
	};

	const head = document.createElement("div");
	head.className = "look-head";
	const said = document.createElement("p");
	said.className = "look-said";
	said.textContent = `"${thing.said}"`;
	const close = document.createElement("button");
	close.type = "button";
	close.className = "look-x";
	close.setAttribute("aria-label", "Close");
	close.textContent = "×";
	close.addEventListener("click", shut);
	head.append(said, close);

	const stage = document.createElement("div");
	stage.className = "look-stage checker";
	const canvas = document.createElement("canvas");
	const frames = renderFrames(thing.spec);
	const scale = fit(thing.spec, 200, 5);
	blit(canvas, frames[0].canvas.toBitmap(), scale);
	stage.append(canvas);

	// Animated sprites play in here too, rather than being shown as a still.
	let timer = 0;
	if (frames.length > 1 && !reduced.matches) {
		let f = 0;
		timer = window.setInterval(() => {
			f = (f + 1) % frames.length;
			blit(canvas, frames[f].canvas.toBitmap(), scale);
		}, 120);
	}
	dialog.addEventListener("close", () => window.clearInterval(timer));

	const kit = kitFrom(thing.spec.kit as never);
	const stats = document.createElement("dl");
	stats.className = "look-stats";
	const roleCount = Object.keys(kit.roles).filter((r) => r !== "none").length;
	for (const [k, v] of [
		["Size", `${thing.spec.size[0]} × ${thing.spec.size[1]}`],
		["Parts", String(thing.spec.parts.length)],
		["Frames", String(frames.length)],
		["Colours", String(roleCount)],
	]) {
		stats.append(
			Object.assign(document.createElement("dt"), { textContent: k }),
			Object.assign(document.createElement("dd"), { textContent: v }),
		);
	}

	// The full parts list, in draw order and not truncated. Nineteen rows saying
	// what a treasure chest is made of is the clearest evidence on the page.
	const parts = document.createElement("ol");
	parts.className = "look-parts";
	for (const [i, part] of thing.spec.parts.entries()) {
		const li = document.createElement("li");
		const n = document.createElement("span");
		n.className = "part-n";
		n.textContent = String(i + 1).padStart(2, "0");
		const sw = document.createElement("span");
		sw.className = "part-sw";
		for (const value of Object.values(part)) {
			if (typeof value === "string" && kit.roles[value] && value !== "none") {
				sw.style.background = css(kit.roles[value]);
				break;
			}
		}
		const what = document.createElement("span");
		what.className = "part-what";
		what.textContent = String(part.shape);
		li.append(n, sw, what);
		parts.append(li);
	}

	const roles = document.createElement("ul");
	roles.className = "look-roles";
	for (const [name, rgba] of Object.entries(kit.roles)) {
		if (name === "none") continue;
		const li = document.createElement("li");
		const sw = document.createElement("span");
		sw.className = "role-sw";
		sw.style.background = css(rgba);
		li.append(sw, Object.assign(document.createElement("span"), { textContent: name }));
		roles.append(li);
	}

	const cols = document.createElement("div");
	cols.className = "look-cols";
	const partsCol = document.createElement("div");
	partsCol.append(Object.assign(document.createElement("p"), { className: "tag", textContent: "Parts, in draw order" }), parts);
	const rolesCol = document.createElement("div");
	rolesCol.append(Object.assign(document.createElement("p"), { className: "tag", textContent: "Colour table" }), roles);
	cols.append(partsCol, rolesCol);

	const go = document.createElement("a");
	go.className = "go small";
	go.href = "/workbench";
	go.append("Open one like it");

	// Two columns: what it looks like on the left, what it is made of on the
	// right. Stacked in one column the sprite ended up small in a very wide box
	// with its own parts list pushed off the bottom of the screen.
	const left = document.createElement("div");
	left.className = "look-left";
	left.append(stage, stats, go);

	const right = document.createElement("div");
	right.className = "look-right";
	right.append(cols);

	dialog.append(head, left, right);
	// Escape, the backdrop and the button all close it. The element is removed
	// each time so the page does not collect dead dialogs.
	dialog.addEventListener("cancel", shut);
	dialog.addEventListener("click", (e) => {
		if (e.target === dialog) shut();
	});
	document.body.append(dialog);
	dialog.showModal();
}

// ---- building the rooms ----------------------------------------------------------

const PART_MS = 32;

/**
 * How far into a surface a thing standing on it sits, as a fraction of the
 * host's height.
 *
 * A guess with a reason rather than a measurement: at this viewing angle the
 * visible depth of a tabletop is roughly a fifth of the whole sprite, so a
 * little under half of that puts the thing on the top rather than on its far
 * rim or through it.
 */
const SURFACE_INSET = 0.18;
/** Between one thing in a room finishing and the next one starting. */
const NEXT_MS = 110;

interface Built {
	el: HTMLElement;
	canvas: HTMLCanvasElement;
	line: HTMLElement;
	thing: Thing;
	scale: number;
	started: boolean;
	/** Whether its room is on screen, asked by the animation ticker. */
	seen: () => boolean;
	/** How far along its own room it stands, in CSS pixels. */
	across: number;
}

let built: Built[] = [];
let roomEls: RoomEl[] = [];
/** Whether a room is wider than the window, which changes how the walk is paced. */
let strolling = false;

interface RoomEl {
	el: HTMLElement;
	name: string;
	line: string;
	items: Built[];
	walked: boolean;
	/** True while any part of it is on screen. Nothing animates in a room you cannot see. */
	seen: boolean;
	/** Wider than the window, so its contents arrive as you reach them rather than in one performance. */
	walkThrough: boolean;
	/** Paints the room and fills its air. Cleared once it has been called. */
	dress: (() => void) | null;
}

/**
 * One shadow spec per sprite width.
 *
 * `shadowSpec` builds a new object every call, so the render cache, which is
 * keyed on the object, never hit for the sixty shadows on the page. Sprites
 * come in a handful of widths, so this is a handful of specs.
 */
const shadows = new Map<number, SpriteSpec>();

function shadowFor(width: number): SpriteSpec {
	let spec = shadows.get(width);
	if (!spec) {
		spec = shadowSpec(width);
		shadows.set(width, spec);
	}
	return spec;
}

/** Paint a room if it has not been painted, wherever the demand came from. */
function dress(room: RoomEl) {
	const job = room.dress;
	if (!job) return;
	room.dress = null;
	job();
}

/**
 * Frames rendered once per spec.
 *
 * Everything here asked the renderer the same question several times over: how
 * tall a table's surface is, how many blank rows a mug has under it, what to
 * draw when it lands, and then what to draw on every animation tick. That is
 * four full renders of every frame of every sprite in five rooms, which is most
 * of what a phone was spending its time on.
 *
 * Keyed on the spec object, so the throwaway part-by-part specs an assembling
 * sprite makes are collected rather than piling up.
 */
const rendered = new WeakMap<SpriteSpec, RenderedFrame[]>();

function framesFor(spec: SpriteSpec): RenderedFrame[] {
	let frames = rendered.get(spec);
	if (!frames) {
		frames = renderFrames(spec);
		rendered.set(spec, frames);
	}
	return frames;
}

/**
 * A frame drawn once into its own canvas, ready to be stamped.
 *
 * `blit` builds an ImageData and an OffscreenCanvas every time it is called,
 * which is fine once and wasteful sixty times a second. An animation bakes its
 * frames when it starts and then only draws images.
 */
function bake(source: { width: number; height: number; data: Uint8Array }): OffscreenCanvas {
	const off = new OffscreenCanvas(source.width, source.height);
	off.getContext("2d")?.putImageData(new ImageData(new Uint8ClampedArray(source.data), source.width, source.height), 0, 0);
	return off;
}

function paint(target: HTMLCanvasElement, baked: OffscreenCanvas, scale: number) {
	target.width = baked.width * scale;
	target.height = baked.height * scale;
	const ctx = target.getContext("2d");
	if (!ctx) return;
	ctx.imageSmoothingEnabled = false;
	ctx.drawImage(baked, 0, 0, target.width, target.height);
}

/**
 * Everything on the page that has more than one frame, driven by one timer.
 *
 * It was one `setInterval` per animated sprite and per drifting cloud, which is
 * around sixty timers all waking independently, all of them still running for
 * rooms four screens away. One timer, and it skips anything whose room is off
 * screen: what you cannot see costs nothing.
 */
interface Anim {
	canvas: HTMLCanvasElement;
	frames: OffscreenCanvas[];
	scale: number;
	at: number;
	room: () => boolean;
}

const anims: Anim[] = [];
let ticking = 0;

function animate(canvas: HTMLCanvasElement, frames: RenderedFrame[], scale: number, room: () => boolean) {
	if (frames.length < 2 || reduced.matches) return;
	const anim: Anim = { canvas, frames: frames.map((f) => bake(f.canvas.toBitmap())), scale, at: 0, room };
	anims.push(anim);
	if (ticking) return;
	ticking = window.setInterval(() => {
		for (const a of anims) {
			if (!a.room()) continue;
			a.at = (a.at + 1) % a.frames.length;
			paint(a.canvas, a.frames[a.at], a.scale);
		}
	}, 120);
}

/**
 * How big one room pixel is on screen.
 *
 * Chosen from the height so a room is a comfortable number of rows tall
 * whatever the window is, and it has to be a whole number or the scene stops
 * being made of pixels.
 */

/**
 * Lay out the five rooms and everything in them.
 *
 * Each room is a scene spec rendered at the size this screen needs, plus its
 * sprites placed in that scene's own grid. Called again on resize, because both
 * the scale and the scene's dimensions come from the viewport.
 */
function makeWorld(done: Set<string>) {
	const world = $("world");
	if (!world) return;

	const { scale, cols, w, h } = roomSize(window.innerWidth, window.innerHeight);

	built = [];
	roomEls = [];
	strolling = w * scale > window.innerWidth * 1.15;
	// Both hold references to elements that are about to be replaced.
	anims.length = 0;
	waiting.length = 0;

	world.replaceChildren(
		...ROOMS.map((room) => {
			const scene = SCENES[room.id](w, h);
			const el = document.createElement("section");
			el.className = "room";
			el.dataset.room = room.id;
			el.style.width = `${w * scale}px`;
			// What a room is worth laying out and painting when it is nowhere near
			// the screen: nothing. Given its real size so that skipping it does not
			// change where any of the others are. See .room in welcome.css.
			el.style.containIntrinsicSize = `${w * scale}px ${h * scale}px`;

			// Made here rather than at the end so everything in the room can ask
			// whether the room is on screen, which is what stops it animating when
			// it is not.
			const mine: RoomEl = {
				el,
				name: room.name,
				line: room.line,
				items: [],
				walked: false,
				seen: false,
				walkThrough: strolling,
				dress: null,
			};
			const onScreen = () => mine.seen;

			// The room itself, drawn by the renderer that draws its contents.
			const back = document.createElement("canvas");
			back.className = "scene";
			back.setAttribute("role", "presentation");
			el.append(back);

			/**
			 * Paint the room and fill its air.
			 *
			 * Held back rather than done here, because five rooms of scenery is five
			 * room-sized renders plus a few hundred tile stamps, and four of them are
			 * for places you are nowhere near. The first room is dressed at once, the
			 * rest one per task afterwards, and anything you reach first is dressed on
			 * arrival. What this buys is the first screen, which is the only one being
			 * waited for.
			 */
			mine.dress = () => {
				const weather: HTMLElement[] = [];
				paintRoom(back, scene, scale, room);

				// What crosses the sky. Each is a generated sprite repeated along a strip
				// twice the room's width; the strip is slid by exactly one width, so the
				// loop is seamless and costs one CSS animation rather than a redraw.
				if (!reduced.matches) {
					for (const d of room.drifts ?? []) {
						const kinds = d.of.map(findSpec).filter((s): s is Thing => Boolean(s));
						if (!kinds.length) continue;
						const lane = document.createElement("div");
						lane.className = "lane";
						lane.setAttribute("aria-hidden", "true");
						lane.style.bottom = `${Math.round(h - scene.line + d.y * scene.line) * scale}px`;
						lane.style.animationDuration = `${d.secs}s`;
						if (d.back) lane.style.animationDirection = "reverse";
						// One half of the lane has to contain a whole number of cycles, or the
						// pattern jumps at the wrap. The spacing is nudged to make it fit.
						const per = Math.max(1, Math.round(w / d.every / kinds.length)) * kinds.length;
						const every = w / per;
						const strip = w * scale;
						lane.style.width = `${strip * 2}px`;
						lane.style.setProperty("--strip", `${strip}px`);
						let nth = 0;
						for (let x = 0; x < w * 2; x += every) {
							// Cycled rather than picked at random, so the second run of the
							// strip is identical to the first and the loop stays seamless.
							const kind = kinds[nth % kinds.length];
							nth += 1;
							const frames = framesFor(kind.spec);
							const c = document.createElement("canvas");
							blit(c, frames[0].canvas.toBitmap(), scale);
							c.className = "adrift";
							c.setAttribute("role", "presentation");
							animate(c, frames, scale, onScreen);
							// A fish drawn facing left has to travel left. Going the other way
							// it swims backwards, so the sprite is mirrored to match.
							if (d.flip) c.style.transform = "scaleX(-1)";
							c.style.left = `${x * scale}px`;
							// Scattered up and down, or a lane of clouds is a dotted line.
							const jig = scatter(Math.round(x % w) + 1);
							c.style.bottom = `${(jig % 13) * scale}px`;
							c.style.marginLeft = `${(jig % 11) * scale}px`;
							lane.append(c);
						}
						weather.push(lane);
					}
				}

				// Whatever this room's air is full of: embers, leaves, dust, bubbles,
				// sparks. Scattered here rather than in the markup so the timings can
				// be staggered and no two ever move in step.
				if (room.air) {
					for (let i = 0; i < room.air.count; i += 1) {
						const bit = document.createElement("span");
						bit.setAttribute("aria-hidden", "true");
						bit.className = `air ${room.air.kind}`;
						bit.style.left = `${(i * 61) % 100}%`;
						bit.style.bottom = `${(i * 23) % 70}%`;
						bit.style.animationDelay = `${(i % 8) * 1.3}s`;
						bit.style.animationDuration = `${9 + (i % 7) * 2}s`;
						weather.push(bit);
					}
				}

				// Stars breathe at their own rate. A field of identical dots pulsing in
				// unison reads as a texture rather than as a sky.
				for (let i = 0; i < (room.stars ?? 0); i += 1) {
					const star = document.createElement("span");
					star.setAttribute("aria-hidden", "true");
					star.className = "star";
					const hx = scatter(i * 2 + 1);
					const hy = scatter(i * 2 + 2);
					star.style.left = `${(hx % 1000) / 10}%`;
					star.style.top = `${((hy % 1000) / 1000) * (room.id === "orbit" ? 92 : 52)}%`;
					star.style.animationDelay = `${(hx % 90) / 10}s`;
					star.style.animationDuration = `${3 + (hy % 50) / 10}s`;
					if (hx % 7 === 0) {
						star.style.width = "3px";
						star.style.height = "3px";
					}
					weather.push(star);
				}

				// Straight after the room and before anything standing in it, which is
				// the order these were written in when they were built here.
				back.after(...weather);
			};

			// No title inside the room. It lived here and lost both ways: tucked
			// behind the contents it was covered by anything tall, and raised above
			// them it hid the lamp instead. It also got clipped by the room's own
			// left edge on the way in. One fixed panel outside the rooms has neither
			// problem, and it is where a game would put it anyway.

			// Where each thing's base sits, in room rows off the bottom. Kept as it
			// goes so a later thing can be stood on an earlier one.
			const baseline = new Map<string, number>();
			const depth = new Map<string, number>();
			const items: Built[] = [];

			for (const placed of room.holds) {
				const thing = findSpec(placed.name);
				if (!thing) continue;

				// The bottom of the tile it stands on. Row 0 is against the wall, so
				// it sits highest up the screen and everything nearer covers it.
				const at = placed.small ? Math.max(1, Math.round(scale / 2)) : scale;
				// Row 0 is the wall itself, not the front edge of the back tile. Putting
				// it a tile forward left everything tall floating clear of the wall with
				// a strip of floor behind it, which is what made the fireplace look
				// dropped in the middle of the room.
				let base = h - scene.line - (placed.row ?? 0) * TILE;
				let z = (placed.row ?? 0) * 3 + (placed.flat ? 8 : 10);

				// A rug lies across its tile rather than standing on the back edge of
				// it, so it is centred on the line instead of sitting above it.
				if (placed.flat) base -= Math.round(thing.spec.size[1] / 2);

				if (placed.wall !== undefined) {
					// Clamped so it cannot climb off the top of the wall. The room canvas
					// is anchored to the floor and cropped at the top, so anything hung
					// too high is simply cut in half.
					const room = scene.line - thing.spec.size[1];
					base = h - scene.line + Math.max(0, Math.min(placed.wall, room));
					// Behind anything standing on the floor, because it is on the wall.
					z = 4;
				}
				if (placed.on) {
					const host = baseline.get(placed.on);
					const hostSpec = findSpec(placed.on);
					// Stood on the host's surface, not on the top of its canvas, and
					// dropped by its own blank rows so it rests rather than hovers.
					//
					// Then sat INTO that surface a little. In three-quarter the topmost
					// opaque row of a table is the far edge of the tabletop, seen across
					// it, so landing exactly there leaves a mug perched on the back rim
					// with the whole top surface visible below it.
					if (host !== undefined && hostSpec) {
						const into = Math.round(hostSpec.spec.size[1] * SURFACE_INSET);
						base = host + surfaceOf(hostSpec.spec) - into - footOf(thing.spec);
					}
					z = (depth.get(placed.on) ?? z) + 1;
				}
				baseline.set(placed.name, base);
				depth.set(placed.name, z);

				const spot = document.createElement("div");
				spot.className = "spot";
				const col = Math.min(cols - 1, Math.max(0, Math.round(placed.x * (cols - 1)) + (placed.dx ?? 0)));
				const across = Math.round((col + 0.5) * TILE * scale);
				spot.style.left = `${across}px`;
				spot.style.bottom = `${base * scale}px`;
				if (placed.small) spot.dataset.small = "1";
				spot.style.zIndex = String(z);

				const said = document.createElement("p");
				said.className = "spot-said";
				said.setAttribute("aria-hidden", "true");

				// Standing on the floor means casting something on it. Without this a
				// sprite reads as pasted in front of the room rather than in it.
				if (placed.wall === undefined && !placed.on && !placed.flat) {
					const shade = shadowFor(thing.spec.size[0]);
					const shadow = document.createElement("canvas");
					shadow.className = "shade";
					shadow.setAttribute("role", "presentation");
					blit(shadow, framesFor(shade)[0].canvas.toBitmap(), at);
					shadow.style.bottom = `${-Math.round((shade.size[1] / 2) * at)}px`;
					spot.append(shadow);
				}

				const plinth = document.createElement("button");
				plinth.type = "button";
				plinth.className = "thing";
				plinth.setAttribute("aria-label", `${thing.said}. See what it is made of.`);
				const canvas = document.createElement("canvas");
				plinth.append(canvas);
				plinth.addEventListener("click", () => open(thing));
				spot.append(plinth);

				const hint = document.createElement("p");
				hint.className = "thing-hint";
				hint.setAttribute("aria-hidden", "true");
				hint.textContent = "Click it";
				spot.append(hint);

				// Everything in a room is drawn at the room's scale. That is what puts
				// the sprites on the same grid as the floor they stand on.
				canvas.width = thing.spec.size[0] * at;
				canvas.height = thing.spec.size[1] * at;

				said.style.left = spot.style.left;
				said.style.bottom = `${base * scale + thing.spec.size[1] * at + 10}px`;
				el.append(said);
				// Hover is now across two elements rather than one parent, so it is
				// wired here rather than left to a CSS descendant selector.
				plinth.addEventListener("pointerenter", () => (said.dataset.peek = "1"));
				plinth.addEventListener("pointerleave", () => delete said.dataset.peek);

				const item: Built = { el: spot, canvas, line: said, thing, scale: at, started: false, seen: onScreen, across };
				// A rebuild after a resize must not replay animations you already
				// watched, so anything already seen goes straight to finished.
				if (done.has(`${room.id}/${placed.name}`)) finish(item);
				items.push(item);
				built.push(item);
				el.append(spot);
			}

			mine.items = items;
			mine.walked = items.every((i) => i.started);
			roomEls.push(mine);
			return el;
		}),
	);

	// The room you are looking at, now. The other four are painted one per task
	// after it, so each is a gap between frames rather than one long freeze, and
	// they are all ready long before the walk arrives.
	dress(roomEls[0]);
	let next = 1;
	const later = () => {
		if (next >= roomEls.length) return;
		dress(roomEls[next]);
		next += 1;
		window.setTimeout(later, 0);
	};
	window.setTimeout(later, 0);
}

/**
 * How far up a sprite its drawn surface actually starts.
 *
 * A canvas is not a solid block: a table 32 rows tall may only draw from row 8
 * down, and stacking a mug on its canvas height leaves the mug hovering a
 * quarter of a tile above the tabletop. The top opaque row is where the surface
 * is, and that is what a thing standing on it should stand on.
 */
function surfaceOf(spec: SpriteSpec): number {
	const bmp = framesFor(spec)[0].canvas.toBitmap();
	for (let y = 0; y < bmp.height; y += 1)
		for (let x = 0; x < bmp.width; x += 1) if (bmp.data[(y * bmp.width + x) * 4 + 3] > 0) return bmp.height - y;
	return bmp.height;
}

/**
 * Empty rows underneath a sprite's drawn content.
 *
 * The other half of standing one thing on another. A mug drawn in the top of
 * its canvas has blank rows below it, and lining its canvas up with the
 * tabletop hangs the mug in the air by exactly that many rows.
 */
function footOf(spec: SpriteSpec): number {
	const bmp = framesFor(spec)[0].canvas.toBitmap();
	for (let y = bmp.height - 1; y >= 0; y -= 1)
		for (let x = 0; x < bmp.width; x += 1) if (bmp.data[(y * bmp.width + x) * 4 + 3] > 0) return bmp.height - 1 - y;
	return 0;
}

/**
 * A scatter that looks unplanned but never changes.
 *
 * Anything of the form (i * k) % n is an arithmetic sequence, so it lays out on
 * a lattice and reads as a pattern rather than as a sky. This is a small integer
 * hash instead: no ordering survives it, and the same index always gives the
 * same answer, so nothing jumps between renders.
 */
function scatter(n: number): number {
	let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
	x ^= x >>> 13;
	x = Math.imul(x, 0xc2b2ae35);
	return Math.abs(x ^ (x >>> 16));
}

/** One sprite drawn once, ready to be stamped across a floor or a wall. */
function stamp(spec: SpriteSpec, scale: number): HTMLCanvasElement {
	const cv = document.createElement("canvas");
	blit(cv, framesFor(spec)[0].canvas.toBitmap(), scale);
	return cv;
}

/**
 * Paint a room: the wash underneath, then its floor and wall tiles stamped over
 * it.
 *
 * The tiles are sprites the tool drew, laid edge to edge, which is how a game
 * builds a room and is the reason this looks like one. Painting them here
 * rather than putting them in the spec is not a compromise: a spec makes one
 * sprite, and a floor is the same sprite four hundred times.
 */
function paintRoom(back: HTMLCanvasElement, scene: Scene, scale: number, room: Room) {
	blit(back, framesFor(scene.spec)[0].canvas.toBitmap(), scale);
	const ctx = back.getContext("2d");
	if (!ctx) return;
	ctx.imageSmoothingEnabled = false;
	const lineY = scene.line * scale;

	const sky = room.skyTile ? findSpec(room.skyTile) : null;
	if (sky) {
		const t = stamp(sky.spec, scale);
		for (let y = lineY - t.height; y > -t.height; y -= t.height)
			for (let x = 0; x < back.width; x += t.width) ctx.drawImage(t, x, y);
	}

	const wall = room.wallTile ? findSpec(room.wallTile) : null;
	if (wall) {
		const t = stamp(wall.spec, scale);
		const top = t.getContext("2d")?.getImageData(0, 0, 1, 1).data;
		// The wall above is filled with the tile's own top colour rather than more
		// tiles: a tile with panelling along its bottom, repeated upwards, gives
		// you a wall made of skirting boards. Outdoors the tile is a low boundary
		// wall and the fill would paint over the sky, so those rooms turn it off.
		if (room.wallRepeat) {
			for (let y = lineY - t.height; y > -t.height; y -= t.height)
				for (let x = 0; x < back.width; x += t.width) ctx.drawImage(t, x, y);
		} else {
			if (top && room.wallFill !== false && !room.skyTile) {
				ctx.fillStyle = `rgb(${top[0]} ${top[1]} ${top[2]})`;
				ctx.fillRect(0, 0, back.width, Math.max(0, lineY - t.height));
			}
			for (let x = 0; x < back.width; x += t.width) ctx.drawImage(t, x, lineY - t.height);
		}
	}

	const floor = room.floorTile ? findSpec(room.floorTile) : null;
	if (floor) {
		const t = stamp(floor.spec, scale);
		for (let y = lineY; y < back.height; y += t.height)
			for (let x = 0; x < back.width; x += t.width) ctx.drawImage(t, x, y);
	}
}

/** Show a thing complete, with no animation. Used on arrival for anyone who has
 *  asked for no motion, and on a rebuild for anything already watched. */
function finish(item: Built) {
	item.started = true;
	item.el.dataset.up = "1";
	// Which lets the caption fade out. A built room should read as a room, and
	// sixteen sentences hanging in the air is a diagram of one.
	item.line.dataset.done = "1";
	item.line.textContent = `"${item.thing.said}"`;
	const frames = framesFor(item.thing.spec);
	blit(item.canvas, frames[0].canvas.toBitmap(), item.scale);
	animate(item.canvas, frames, item.scale, item.seen);
}

/**
 * Build one thing where it stands: the sentence types out, then the parts land
 * one at a time. Watching it arrive in pieces shows what the format is better
 * than describing it.
 *
 * Resolves when the thing is finished, so a room can bring its contents in one
 * after another rather than all at once.
 */
function raise(item: Built): Promise<void> {
	if (item.started) return Promise.resolve();
	item.started = true;
	item.el.dataset.up = "1";
	item.line.dataset.up = "1";

	if (reduced.matches) {
		finish(item);
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const full = item.thing.said;
		let typed = 0;
		const type = () => {
			typed += 1;
			item.line.textContent = `"${full.slice(0, typed)}`;
			// The parts start landing before the sentence has finished, so there is
			// never a stretch of empty floor under a half-typed line.
			if (typed === Math.floor(full.length * 0.45)) grow();
			if (typed < full.length) {
				window.setTimeout(type, 11);
				return;
			}
			item.line.textContent = `"${full}"`;
		};

		let parts = 0;
		const grow = () => {
			parts += 1;
			try {
				// Only the first frame: what is on screen while a sprite assembles is
				// frame 0, and rendering the other seven to discard them is most of
				// the cost of the whole animation.
				blit(
					item.canvas,
					bitmapOf({ ...item.thing.spec, parts: item.thing.spec.parts.slice(0, parts) }),
					item.scale,
				);
			} catch {
				// A part that cannot draw alone must not stop the rest arriving.
			}
			if (parts < item.thing.spec.parts.length) {
				window.setTimeout(grow, PART_MS);
				return;
			}
			// The caption stays up for a moment after the sprite lands, so it can be
			// read, then fades. Hovering brings it back.
			window.setTimeout(() => {
				item.line.dataset.done = "1";
			}, 900);
			resolve();
			const frames = framesFor(item.thing.spec);
			// The last part landed from a one-frame render, so redraw the finished
			// sprite from the full set before handing it to the ticker.
			blit(item.canvas, frames[0].canvas.toBitmap(), item.scale);
			animate(item.canvas, frames, item.scale, item.seen);
		};

		type();
	});
}

/**
 * Things waiting for the walk to reach them.
 *
 * Checked once per frame rather than on a timer of their own, because the thing
 * they are all waiting on is where the page has been scrolled to.
 */
const waiting: { ready: () => boolean; go: () => void }[] = [];

function until(ready: () => boolean): Promise<void> {
	if (ready()) return Promise.resolve();
	const held = new Promise<void>((go) => waiting.push({ ready, go: () => go() }));
	pump();
	return held;
}

/** Let go of anything whose turn has come. */
function release() {
	for (let i = waiting.length - 1; i >= 0; i -= 1) {
		if (!waiting[i].ready()) continue;
		waiting.splice(i, 1)[0].go();
	}
}

/**
 * Keep checking while anything is waiting.
 *
 * These were only checked on scroll, which is wrong in the one case that
 * matters: you walk into a room, the first thing starts building, and you stop
 * to watch. No more scroll events, so nothing ever released the second thing and
 * the room stayed half furnished until you moved again.
 */
let pumping = false;

function pump() {
	if (pumping) return;
	pumping = true;
	const step = () => {
		release();
		if (waiting.length) requestAnimationFrame(step);
		else pumping = false;
	};
	requestAnimationFrame(step);
}

/**
 * Bring a room's contents in one after another, in the order they are placed.
 *
 * A room that fits on screen furnishes as a performance: you arrive, and it
 * fills. A room wider than the window cannot do that, since half of it would be
 * built out of sight and finished before you got there, so each thing waits
 * until the walk reaches it. Same order either way.
 */
async function furnish(room: RoomEl) {
	for (const item of room.items) {
		if (room.walkThrough) await until(() => item.el.getBoundingClientRect().left < window.innerWidth * 0.9);
		await raise(item);
		await new Promise((r) => window.setTimeout(r, NEXT_MS));
	}
}

/**
 * Move the world as the page scrolls.
 *
 * How far down the section you are is how far along the strip you have gone.
 * That one number drives the world offset, the progress bar, which room's name
 * is showing, and which room has been walked into.
 */
function walk(done: Set<string>) {
	const level = $("level");
	const world = $("world");
	const fill = $("progressFill");
	const hud = $("hudRoom");
	const hudLine = $("hudLine");
	if (!level || !world) return;

	let travel = 0;
	const measure = () => {
		// How far the world has to slide for its far end to reach the far edge.
		travel = Math.max(0, world.scrollWidth - window.innerWidth);
		// Tall enough that the walk is not a twitch: roughly a screen of scrolling
		// per screen of world, plus some to arrive and some to leave.
		//
		// Except where a room is wider than the window. There the world is a dozen
		// screens across, and matching it one to one buries everything after the
		// rooms under a scroll nobody finishes: the price list went from three
		// screens down the page to nearly seven, and read as missing. Half pace on
		// those screens keeps the page about as long as it was while the rooms stay
		// their full width.
		const pace = strolling ? 0.5 : 1;
		level.style.height = `${window.innerHeight + travel * pace + window.innerHeight * 0.4}px`;
	};

	let showing = "";
	const frame = () => {
		const box = level.getBoundingClientRect();
		const run = level.offsetHeight - window.innerHeight;
		const gone = Math.min(Math.max(-box.top / (run || 1), 0), 1);

		world.style.transform = `translateX(${-gone * travel}px)`;
		if (fill) fill.style.width = `${gone * 100}%`;

		const wide = window.innerWidth;
		for (const room of roomEls) {
			const at = room.el.getBoundingClientRect();
			// What the animation ticker reads. A room off the side of the screen
			// stops redrawing entirely, which on a phone is four rooms out of five.
			room.seen = at.right > -80 && at.left < wide + 80;
			// Scrolled to faster than the queue got to it.
			if (room.dress && at.right > -wide && at.left < wide * 2) dress(room);

			// Whichever room is under the middle of the screen owns the label.
			if (at.left < wide / 2 && at.right > wide / 2 && showing !== room.name) {
				showing = room.name;
				if (hud) hud.textContent = room.name;
				if (hudLine) hudLine.textContent = room.line;
			}

			// A room starts furnishing once it is arrived at rather than each thing
			// waiting for its own edge. Arriving somewhere and watching it fill is
			// the idea; four separate reveals as you pan is not. A room too wide to
			// see all of paces itself against the walk instead, in furnish.
			if (!room.walked && at.left < wide * (room.walkThrough ? 0.95 : 0.55) && at.right > 0) {
				room.walked = true;
				void furnish(room);
			}
		}

		// Whatever was waiting for the walk to reach it.
		release();
	};

	// Scroll fires far more often than the screen is redrawn, and every frame here
	// reads layout for five rooms. Coalesced, it runs once per painted frame.
	let queued = false;
	const onScroll = () => {
		if (queued) return;
		queued = true;
		requestAnimationFrame(() => {
			queued = false;
			frame();
		});
	};

	measure();
	frame();
	addEventListener("scroll", onScroll, { passive: true });

	// A resize changes the scale and the scene's dimensions, so the rooms are
	// rebuilt rather than stretched. What has already been watched stays built.
	let pending = 0;
	addEventListener("resize", () => {
		window.clearTimeout(pending);
		pending = window.setTimeout(() => {
			for (const item of built) if (item.started) done.add(key(item));
			makeWorld(done);
									measure();
			frame();
		}, 200);
	});
}

/** Which room a built thing belongs to, so a rebuild can tell what was watched. */
const key = (item: Built) => {
	const room = item.el.closest(".room") as HTMLElement | null;
	return `${room?.dataset.room ?? "?"}/${item.thing.spec.name}`;
};

/**
 * The opening demonstration.
 *
 * A screen of claims is a screen people leave, so the claims sit next to the
 * product doing the thing they describe: the sentence types out, the sprite
 * lands a piece at a time, and the count says how many pieces there were. It
 * reuses the same renderer and the same specs as the rooms below, so it cannot
 * advertise anything the rest of the page does not do.
 */
function openingDemo() {
	const canvas = $<HTMLCanvasElement>("demoArt");
	const said = $("demoSaid");
	const count = $("demoCount");
	if (!canvas || !said || !count) return;

	// Chosen to look different from one another: a fire, a market stall, a hoard,
	// a console. Anything missing is skipped rather than left as a hole.
	const cast = ["fireplace", "market_stall", "treasure_chest_sunk", "control_console", "apple_tree"]
		.map(findSpec)
		.filter((t): t is Thing => Boolean(t));
	if (!cast.length) return;

	if (reduced.matches) {
		const only = cast[0];
		said.textContent = `"${only.said}"`;
		blit(canvas, bitmapOf(only.spec), fit(only.spec, 210, 6));
		count.textContent = `${only.spec.parts.length} pieces`;
		return;
	}

	// The five sentences are different lengths, so the box grew and shrank as each
	// one typed itself out and shoved the sprite, the count and every section below
	// down the page. Hold the tallest of them from the start. Measured rather than
	// guessed a line at a time in CSS, because each one wraps differently at every
	// width, and re-measured when the width changes.
	const talk = said.parentElement ?? said;
	let lastWide = -1;
	const reserve = () => {
		const wide = said.clientWidth;
		if (wide === lastWide) return;
		lastWide = wide;
		const showing = said.textContent;
		said.style.minHeight = "0px";
		let tallest = 0;
		for (const t of cast) {
			said.textContent = `"${t.said}"`;
			tallest = Math.max(tallest, said.offsetHeight);
		}
		said.textContent = showing;
		said.style.minHeight = `${tallest}px`;
	};
	reserve();
	// Watching the row rather than the paragraph: setting the paragraph's own
	// height inside the callback would retrigger it forever. The width guard is
	// what stops the row's own height change from doing the same.
	new ResizeObserver(reserve).observe(talk);

	// The hero keeps drawing forever, and it is a screen and a half above the
	// rooms by the time anybody is reading them. Off screen it stops, and picks
	// up where it left off when it comes back.
	let awake = true;
	let running = false;
	// Told whether the loop is going rather than whether it is asleep. The first
	// version tracked the sleeping half and had two ways to stop and one way to
	// start, so a stop the restart did not know about left the hero frozen
	// mid-sentence for good.
	const eye = new IntersectionObserver(
		([seen]) => {
			awake = seen.isIntersecting;
			if (awake && !running) run();
		},
		{ rootMargin: "120px" },
	);
	eye.observe(canvas);

	let which = 0;
	const run = () => {
		if (!awake) {
			running = false;
			return;
		}
		running = true;
		const thing = cast[which % cast.length];
		which += 1;
		const scale = fit(thing.spec, 210, 6);
		// The canvas is deliberately NOT cleared here. Resizing it blanks it, and
		// the sentence takes a second to type, so the stage would sit empty between
		// every sprite. The last one holds until the first piece of the next lands.
		count.textContent = "";

		const full = thing.said;
		let typed = 0;
		let parts = 0;

		const grow = () => {
			parts += 1;
			try {
				// Frame 0 only. What is on screen mid-assembly is one frame, and the
				// other seven are rendered and thrown away thirty times a second.
				blit(
					canvas,
					bitmapOf({ ...thing.spec, parts: thing.spec.parts.slice(0, parts) }),
					scale,
				);
			} catch {
				// A piece that cannot draw on its own must not stop the rest.
			}
			count.textContent = `${parts} of ${thing.spec.parts.length} pieces`;
			if (parts < thing.spec.parts.length) {
				if (awake) window.setTimeout(grow, 34);
				else running = false;
				return;
			}
			// Finished, so let it play if it moves, then move on.
			const frames = framesFor(thing.spec);
			blit(canvas, frames[0].canvas.toBitmap(), scale);
			let timer = 0;
			if (frames.length > 1 && !reduced.matches) {
				const baked = frames.map((fr) => bake(fr.canvas.toBitmap()));
				let f = 0;
				timer = window.setInterval(() => {
					if (!awake) return;
					f = (f + 1) % baked.length;
					paint(canvas, baked[f], scale);
				}, 120);
			}
			window.setTimeout(() => {
				window.clearInterval(timer);
				run();
			}, 2600);
		};

		const type = () => {
			typed += 1;
			said.textContent = `"${full.slice(0, typed)}`;
			if (typed === Math.floor(full.length * 0.4)) grow();
			if (typed < full.length) {
				// Same as grow: stopping without saying so is how a paused loop
				// becomes a dead one, since nothing would restart it.
				if (awake) window.setTimeout(type, 22);
				else running = false;
				return;
			}
			said.textContent = `"${full}"`;
		};
		type();
	};
	run();
}

/**
 * Tile a generated sprite across a section's background.
 *
 * The bookend pages were flat panels of web-page grey against five rooms made
 * of pixels, and looked borrowed from a different site. This gives them the
 * same material as everything else: a tile the tool drew, laid edge to edge.
 */
/**
 * The plates the page is bolted to, and the rare thing set into one.
 *
 * The background was one tile repeated, and one tile repeated is a grid: the eye
 * finds the period in about a second and the whole page reads as graph paper.
 * Four plates shuffled kills the period, and a detail every fifteenth tile or so
 * gives it somewhere to look without becoming wallpaper.
 *
 * They are hull plates because the mascot is a robot. A character standing in
 * front of a plain charcoal wash is a sprite on a web page; the same character
 * in front of a wall he could plausibly have walked past is a place.
 */
const PLATES = ["hull_plate_a", "hull_plate_b", "hull_plate_c", "hull_plate_d"];
const FITTINGS = ["hull_vent", "hull_conduit", "hull_stencil"];
/**
 * One tile in this many carries a fitting. Higher is quieter.
 *
 * Started at 15 and the wall read as an engine room somebody had to look at.
 * At 26 a vent is a thing you notice on the second look, which is what a
 * background is for.
 */
const FITTED = 26;

function backdrop(id: string, tile: string, band?: string) {
	const canvas = $<HTMLCanvasElement>(id);
	if (!canvas) return;
	const plates = PLATES.map(findSpec).filter((s): s is Thing => Boolean(s));
	const fittings = FITTINGS.map(findSpec).filter((s): s is Thing => Boolean(s));
	// The old single tile, still the fallback: a missing plate should give a
	// duller wall rather than a hole.
	const plain = findSpec(tile);
	if (!plates.length && !plain) return;

	const paint = () => {
		const scale = 4;
		const kinds = (plates.length ? plates : [plain as Thing]).map((s) => stamp(s.spec, scale));
		const extras = fittings.map((s) => stamp(s.spec, scale));
		const t = kinds[0];
		const box = canvas.parentElement?.getBoundingClientRect();
		canvas.width = Math.ceil((box?.width ?? window.innerWidth) / t.width) * t.width;
		canvas.height = Math.ceil((box?.height ?? window.innerHeight) / t.height) * t.height;
		// Shown at exactly the size it was drawn, and the section clips whatever
		// hangs over. Left to fill its box with `height: 100%`, a wall of whole
		// tiles gets squashed into a box that is not a whole number of them, so
		// every plate comes out a different height to the plate it was drawn as.
		canvas.style.width = `${canvas.width}px`;
		canvas.style.height = `${canvas.height}px`;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.imageSmoothingEnabled = false;
		let cell = 0;
		for (let y = 0; y < canvas.height; y += t.height) {
			for (let x = 0; x < canvas.width; x += t.width) {
				// Hashed rather than random, so a resize repaints the same wall. A wall
				// that reshuffles itself when the window changes is a wall nobody
				// believes in.
				const roll = scatter(cell);
				cell += 1;
				const fitting = extras.length && roll % FITTED === 0 ? extras[(roll >> 8) % extras.length] : null;
				ctx.drawImage(fitting ?? kinds[roll % kinds.length], x, y);
			}
		}

		// One unbroken run of the same tile across the middle, for the section that
		// is a threshold rather than a place. A lit strip going the whole way across
		// reads as a doorway; the same strip broken up by plates reads as a fault.
		const strip = band ? findSpec(band) : null;
		if (strip) {
			const s = stamp(strip.spec, scale);
			const row = Math.round((canvas.height - s.height) / 2 / s.height) * s.height;
			for (let x = 0; x < canvas.width; x += s.width) ctx.drawImage(s, x, row);
		}
	};

	paint();

	/**
	 * Repaint whenever the section changes size, not only when the window does.
	 *
	 * These are painted at whatever size the section is when the script runs,
	 * which on a page with a webfont and a heading that wraps is not its final
	 * size. The threshold section came out 500 pixels of wall stretched over 750
	 * pixels of section: every plate a third taller than a plate, on a page whose
	 * whole argument is hard pixels. A window resize was the only thing that
	 * corrected it, which is not something a visitor does.
	 */
	const box = canvas.parentElement;
	if (box && "ResizeObserver" in window) {
		let pending = 0;
		new ResizeObserver(() => {
			window.clearTimeout(pending);
			pending = window.setTimeout(paint, 120);
		}).observe(box);
		return;
	}

	let pending = 0;
	addEventListener("resize", () => {
		window.clearTimeout(pending);
		pending = window.setTimeout(paint, 200);
	});
}

const watched = new Set<string>();
backdrop("openingBack", "page_tile");
backdrop("costsBack", "page_tile");
backdrop("segueBack", "page_tile", "hull_conduit");
openingDemo();
void fillPacks();
makeWorld(watched);
walk(watched);
